import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import {
  WarningAmber as WarningIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { stopImpersonation } from '../services/superAdminService';
import { forceLogout } from '../services/api';

/**
 * Bandeau permanent affiché pendant une impersonation (UI-6).
 *
 * ── POURQUOI EN BAS DE L'ÉCRAN, ET NON EN HAUT ───────────────────────────────
 * Le réflexe serait de le poser en haut, au-dessus du Header. NE PAS LE FAIRE sans
 * relire ce qui suit : ce placement a été mesuré et écarté.
 *
 * Deux des trois éléments de la mise en page sont `position: fixed` et ignorent donc
 * le flux — un bandeau inséré au-dessus passerait DESSOUS :
 *   - Header.js      -> StyledAppBar `position="fixed"`, aucun `top` (donc top: 0)
 *   - Sidebar.js     -> StyledDrawer `variant="permanent"` : MUI rend le paper en
 *                       `position: fixed; top: 0; height: 100%`, sans override ici
 *   - App.js         -> <Main> dans le flux, avec un <DrawerHeader/> fantôme qui
 *                       compense la seule hauteur du Header
 *
 * Décaler réellement le contenu exigerait de modifier CES TROIS FICHIERS (ajouter un
 * `top` à l'AppBar, un `top` + `height: calc(100% - X)` au paper du Drawer, un second
 * fantôme dans Main) et de partager entre eux une CONSTANTE DE HAUTEUR EN DUR. Or ni
 * Header ni Sidebar n'acceptent de prop `sx` : il faudrait les rendre conscients de
 * l'existence de ce bandeau. Et cette hauteur varie avec la LANGUE — un texte qui passe
 * sur deux lignes en hébreu ou en anglais désaligne toute la mise en page, en silence.
 *
 * En bas, en `fixed` : deux fichiers touchés (celui-ci et App.js), aucun couplage à une
 * hauteur, et surtout — le bandeau fonctionne À L'IDENTIQUE sur le cul-de-sac « aucun
 * stock sélectionné », qui n'a NI Header NI Sidebar. C'est précisément là qu'atterrit
 * une impersonation vers un utilisateur sans stock, donc là où le bandeau est vital.
 *
 * Le déficit de visibilité du bas est compensé : couleur d'alerte pleine, largeur
 * totale, AUCUN bouton de fermeture, et l'action de sortie directement dedans.
 *
 * ── CONDITION D'AFFICHAGE ────────────────────────────────────────────────────
 * `impersonated` ET `user`. Un bandeau sans utilisateur serait un fantôme : il ne
 * pourrait nommer personne, ce qui est justement son unique raison d'être.
 */

/** Hauteur réservée sous le contenu pour que le bandeau ne masque pas le bas de page.
 *  Unités de spacing MUI (10 = 80px) : généreux, le bandeau peut passer sur 2 lignes. */
export const BANNER_SPACER = 10;

const ImpersonationBanner = ({ user, impersonated, onEnded }) => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Hooks appelés avant toute sortie anticipée (règle des hooks React).
  if (!impersonated || !user) {
    return null;
  }

  const handleStop = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await stopImpersonation();
      onEnded(data.user);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        // « Aucune impersonation en cours » : le bandeau MENTAIT — l'état local était
        // périmé. Rien à restaurer côté serveur, on purge et il disparaît.
        onEnded(null);
      } else if (status === 403) {
        // L'impersonateur n'est plus SUPER_ADMIN, ou est désactivé. L'utilisateur est
        // COINCÉ dans l'identité de la cible : aucun re-rendu ne l'en sortira.
        // On affiche le message serveur, puis on force la sortie — le délai laisse le
        // temps de le lire, sans quoi le reload l'effacerait aussitôt.
        setError(err?.response?.data?.error || t('superAdmin.impersonatorNoLongerValid'));
        setTimeout(forceLogout, 4000);
      } else {
        setError(err?.response?.data?.error || err?.message || t('superAdmin.backToAccountError'));
        setBusy(false);
      }
    }
  };

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        // Au-dessus de l'AppBar (drawer + 1) : rien ne doit le recouvrir.
        zIndex: (theme) => theme.zIndex.drawer + 2,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <WarningIcon />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t('superAdmin.impersonatingAs', { name: user.full_name || user.email })}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
          {error || t('superAdmin.impersonationBannerHint')}
        </Typography>
      </Box>
      <Button
        variant="contained"
        color="inherit"
        size="small"
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <BackIcon />}
        onClick={handleStop}
        disabled={busy}
        sx={{ color: 'warning.main', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        {t('superAdmin.backToMyAccount')}
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
